import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { ActiveSession } from '@/hooks/useDashboardData';
import { Badge } from '@/components/ui/badge';
import { Zap, ArrowRight, Clock } from 'lucide-react';
import { differenceInDays } from 'date-fns';

interface AttentionQueueProps {
  sessions: ActiveSession[];
}

interface AttentionItem {
  session: ActiveSession;
  reason: string;
  urgency: 'critical' | 'warning';
}

function getAttentionItems(sessions: ActiveSession[]): AttentionItem[] {
  const items: AttentionItem[] = [];

  for (const session of sessions) {
    if (session.subphase === '3.4') continue; // already committed
    if (session.my_role !== 'owner' && session.my_role !== 'contributor') continue;

    const daysSince = differenceInDays(new Date(), new Date(session.updated_at));

    // Critical: Phase 3 stagnating >7 days
    if (session.phase === 3 && daysSince > 7) {
      items.push({
        session,
        reason: `Phase 3 seit ${daysSince} Tagen inaktiv – Commit möglich`,
        urgency: 'critical',
      });
      continue;
    }

    // Critical: Any session >30 days
    if (daysSince > 30) {
      items.push({
        session,
        reason: `${daysSince} Tage inaktiv – abschließen oder archivieren`,
        urgency: 'critical',
      });
      continue;
    }

    // Warning: Stagnating >14 days
    if (daysSince > 14) {
      items.push({
        session,
        reason: `Seit ${daysSince} Tagen keine Aktivität`,
        urgency: 'warning',
      });
    }
  }

  // Sort: critical first, then by days inactive desc
  items.sort((a, b) => {
    if (a.urgency !== b.urgency) return a.urgency === 'critical' ? -1 : 1;
    return differenceInDays(new Date(), new Date(a.session.updated_at)) -
           differenceInDays(new Date(), new Date(b.session.updated_at));
  });

  return items.slice(0, 5);
}

function getTitle(session: ActiveSession): string {
  if (session.summary) return session.summary;
  if (session.decision_type && session.decision_subtype) {
    return `${session.decision_type} – ${session.decision_subtype}`;
  }
  return session.decision_type || 'Neue Entscheidung';
}

export const AttentionQueue = ({ sessions }: AttentionQueueProps) => {
  const navigate = useNavigate();
  const items = useMemo(() => getAttentionItems(sessions), [sessions]);

  if (items.length === 0) return null;

  return (
    <div className="rounded-2xl border border-primary/20 bg-primary/[0.02] overflow-hidden">
      <div className="flex items-center gap-2 px-3 sm:px-4 py-2.5 border-b border-primary/10">
        <Zap className="h-3.5 w-3.5 text-primary" />
        <span className="text-xs sm:text-sm font-semibold text-foreground">Jetzt handeln</span>
        <Badge variant="secondary" className="h-4 text-[9px] px-1.5 bg-primary/10 text-primary border-primary/20">
          {items.length}
        </Badge>
      </div>

      <div className="divide-y divide-primary/10">
        {items.map((item) => (
          <div
            key={item.session.id}
            className="group flex items-center gap-3 px-3 sm:px-4 py-2.5 hover:bg-primary/[0.04] transition-colors cursor-pointer"
            onClick={() => navigate(`/app/session/${item.session.id}`)}
          >
            <div className={`w-2 h-2 rounded-full flex-shrink-0 ${
              item.urgency === 'critical' ? 'bg-destructive animate-pulse' : 'bg-amber-500'
            }`} />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-foreground truncate group-hover:text-primary transition-colors">
                {getTitle(item.session)}
              </p>
              <div className="flex items-center gap-1.5 mt-0.5">
                <Clock className="h-3 w-3 text-muted-foreground flex-shrink-0" />
                <span className="text-[10px] sm:text-xs text-muted-foreground truncate">{item.reason}</span>
              </div>
            </div>
            <Badge variant="outline" className="text-[9px] sm:text-[10px] capitalize flex-shrink-0">
              {item.session.my_role}
            </Badge>
            <ArrowRight className="h-3.5 w-3.5 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0" />
          </div>
        ))}
      </div>
    </div>
  );
};
